import { useGetTosContent } from '@sib/api';
import { useTranslation } from '@sib/i18n';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@sib/ui';
import { X } from '@sib/ui/icons';
import { DEFAULT_DATE_FORMAT } from '@spendbrain/config/constants';
import { format } from 'date-fns';
import { TermsOfServiceModalSkeleton } from './TermsOfServiceModalSkeleton';
import { TOS_CONTENT } from './TermsOfServiceModal.content';
import {
  LexicalNode,
  lexicalToHtml,
  useScrolledToBottom,
} from './TermsOfServiceModal.utils';

interface TermsOfServiceModalProps {
  open: boolean;
  onAccept: () => void;
  onClose: () => void;
  canAccept?: boolean;
  isDismissible?: boolean;
  acceptedByUser?: {
    name: string;
    acceptedAt: Date | string;
  } | null;
}

export const TermsOfServiceModal = ({
  open,
  acceptedByUser,
  canAccept = true,
  isDismissible = false,
  onAccept,
  onClose,
}: TermsOfServiceModalProps) => {
  const { t } = useTranslation();
  const { hasScrolledToBottom, handleScroll, handleContentMount } =
    useScrolledToBottom();
  const { data, isLoading } = useGetTosContent();

  const contentHtml = data
    ? lexicalToHtml(data.tosContent as LexicalNode[])
    : null;

  return (
    <Dialog open={open}>
      <DialogContent className="max-w-181.75 gap-7 rounded-2xl px-12.5 py-7">
        {isDismissible && (
          <DialogClose
            onClick={onClose}
            className="absolute top-7 right-12.5 cursor-pointer rounded-sm opacity-70 transition-opacity hover:opacity-100"
          >
            <X className="text-primary size-5" />
            <span className="sr-only">{t('close')}</span>
          </DialogClose>
        )}
        <DialogHeader className="flex flex-col gap-7">
          <DialogTitle className="text-primary text-center text-xl">
            {t('termsOfServiceModal.title')}
          </DialogTitle>
          <DialogDescription className="text-primary text-center text-sm leading-5">
            {t('termsOfServiceModal.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">
            {t('termsOfServiceModal.title')}
          </h3>
          <div
            ref={handleContentMount}
            onScroll={handleScroll}
            className="border-border max-h-[265px] overflow-y-auto rounded-sm border px-3.5 py-3 text-sm"
          >
            {isLoading ? (
              <TermsOfServiceModalSkeleton />
            ) : contentHtml ? (
              <div dangerouslySetInnerHTML={{ __html: contentHtml }} />
            ) : (
              <span className="whitespace-pre-line">{TOS_CONTENT}</span>
            )}
          </div>
        </div>

        {acceptedByUser ? (
          <div className="border-border border-t-1.5 flex items-center justify-between border-t pt-3">
            <div className="flex items-center gap-4.5">
              <div className="flex flex-col gap-1.5">
                <p className="text-neutral-1400 text-sm font-normal">
                  {t('termsOfServiceModal.signatory')}
                </p>
                <p className="text-primary text-sm font-medium">
                  {acceptedByUser.name}
                </p>
              </div>
              <div className="bg-border w-px self-stretch" />
              <div className="flex flex-col gap-1.5">
                <p className="text-neutral-1400 text-sm font-normal">
                  {t('date')}
                </p>
                <p className="text-primary text-sm font-medium">
                  {format(acceptedByUser.acceptedAt, DEFAULT_DATE_FORMAT)}
                </p>
              </div>
            </div>

            <Button onClick={onClose}>{t('continue')}</Button>
          </div>
        ) : (
          <div className="flex justify-center">
            <Button
              onClick={onAccept}
              disabled={!hasScrolledToBottom || !canAccept}
            >
              {t('termsOfServiceModal.accept')}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
